import React, { useEffect, useState } from 'react';
import ExamQuestionAccordion from './ExamQuestionAccordion';

const API_BASE = process.env.REACT_APP_API_URL;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function getScoreColour(score, max) {
  const pct = score / max;
  if (pct >= 0.75) return { text: '#3fb950', bg: 'rgba(63,185,80,0.10)',  border: '#3fb950' };
  if (pct >= 0.5)  return { text: '#d29922', bg: 'rgba(210,153,34,0.10)', border: '#d29922' };
  return             { text: '#f85149', bg: 'rgba(248,81,73,0.10)',  border: '#f85149' };
}

export default function ExamReviewDetail({ examId, getToken, onBack, onClose }) {
  const [exam, setExam]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/api/exams/${examId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setExam(data);
      } catch (err) {
        if (!cancelled) setError('Could not load exam details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [examId, getToken]);

  const c = exam?.totalScore != null ? getScoreColour(exam.totalScore, exam.maxScore) : null;

  return (
    <>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-5 py-3 border-b border-[#30363d] bg-[#0d1117]">
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[#8b949e] hover:text-[#e6edf3] transition-colors text-xs font-mono"
          aria-label="Back to exam list"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          My Exams
        </button>

        {/* Title */}
        <span className="flex-1 text-xs font-mono text-[#8b949e] truncate">
          {exam ? formatDate(exam.submittedAt) : ''}
        </span>

        {/* Score badge */}
        {c && exam.totalScore != null && (
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
            style={{ borderColor: c.border, backgroundColor: c.bg }}
          >
            <span className="text-xs font-bold font-mono" style={{ color: c.text }}>
              {exam.totalScore}/{exam.maxScore}
            </span>
            <span className="text-[10px] font-mono" style={{ color: c.text }}>
              {exam.percentage}%
            </span>
          </div>
        )}

        {/* Close */}
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded-full text-[#484f58] hover:text-[#e6edf3] hover:bg-[#30363d] transition-colors text-xs"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <span className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-[#f85149] text-xs font-mono text-center mt-8">{error}</div>
        )}

        {!loading && !error && exam && (
          <div className="space-y-3">
            {exam.attempts.map((attempt, i) => (
              <ExamQuestionAccordion
                key={attempt.id}
                index={i}
                attempt={attempt}
              />
            ))}

            {/* Summary footer */}
            {exam.totalScore != null && (
              <div
                className="rounded-xl border-2 px-5 py-4 text-center mt-2"
                style={{ borderColor: c.border, backgroundColor: c.bg }}
              >
                <div className="text-[10px] font-mono text-[#8b949e] uppercase tracking-wider mb-1">Final Score</div>
                <div className="text-3xl font-bold" style={{ color: c.text }}>
                  {exam.totalScore}/{exam.maxScore}
                </div>
                <div className="text-xs text-[#8b949e] mt-1">
                  {exam.percentage}% · {exam.attempts.length} questions
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
